import { useTranslation } from "react-i18next"

import { Badge } from "@/components/ui/badge"
import { cn } from "@/lib/utils"
import type { SenderRoleSnapshot } from "@/types/messaging"

/**
 * OKS-37 — Sender role pill rendered next to the sender's name on
 * each message bubble. Snapshot value is stable across role/tier
 * renames (PRD §3.5 — the snapshot is captured at write-time on the
 * Message row).
 *
 * OKS-763 — `onPrimary` makes the pill bubble-variant-aware. On an
 * outgoing ("mine") orange `bg-primary` bubble the fixed neutral
 * `secondary` badge reads gray/low-contrast, so `onPrimary` swaps it
 * for a border-only treatment (transparent fill +
 * `border-primary-foreground/40` + `text-primary-foreground`) — white
 * text directly on the orange hits the 3.22:1 body-text baseline (the
 * ceiling for white-on-`--primary`; a translucent fill would lighten
 * the orange under the glyphs and *lower* contrast, so we keep the
 * fill transparent). The `outline` base has no `secondary` class
 * residue so the override is clean. Incoming bubbles pass no prop and
 * keep the byte-identical `secondary` badge. The
 * `messaging-role-pill-{role}` test id and the i18n label are
 * preserved in both treatments.
 */
interface RolePillProps {
  role: SenderRoleSnapshot
  onPrimary?: boolean
}

export function RolePill({ role, onPrimary = false }: RolePillProps) {
  const { t } = useTranslation()
  return (
    <Badge
      variant={onPrimary ? "outline" : "secondary"}
      className={cn(
        "px-1.5 py-0 text-[10px]",
        onPrimary && "border-primary-foreground/40 text-primary-foreground",
      )}
      data-testid={`messaging-role-pill-${role}`}
    >
      {t(`messaging.role_badge.${role}`)}
    </Badge>
  )
}
